import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { displayName } from '@promanage/contracts/person-display';
import { ROLE_SUBJECT, formatRef, parseRef, type RoleSubject } from '@promanage/core';
import { Button } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell, ToolbarToggle } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import {
  personColumns,
  DEFAULT_SORT,
  primaryPhone,
  type ColumnId,
} from '../features/people/columns';
import { isCurrent } from '../features/people/detail';
import { PeopleNav } from '../features/people/PeopleNav';
import { todayIso } from '../features/property/today';
import type { Person } from '../features/people/types';
import { trpc } from '../lib/trpc';
import { usePortfolioScope } from '../features/portfolio/scope';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import {
  workspaceChoice,
  workspaceFlag,
  workspaceTable,
  workspaceText,
} from '../features/workspaces/state';

export const Route = createFileRoute('/people/')({
  component: PeopleList,
});

const STORAGE_KEY = 'promanage:people-columns';

/**
 * A stable empty list for the pending state.
 *
 * `list.data ?? []` looks harmless and is not: it builds a NEW array on every render while the
 * query is loading, which changes the identity of the `useMemo` dependency below and re-sorts
 * and re-filters the whole table each time. One shared frozen array has one identity.
 */
const NO_PEOPLE: Person[] = [];

type SubjectFilter = 'all' | RoleSubject;

const FILTERS: { value: SubjectFilter; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'tenancy', label: 'Tenancies' },
  { value: 'owner', label: 'Owners' },
  { value: 'supplier', label: 'Contractors' },
];

/**
 * The people list.
 *
 * One row per human, not per role — a person who is a tenant here and an owner there appears
 * once. The table, the column picker and the toolbar are the shared list kit in
 * `features/table`; what belongs to this page is which columns exist, what "search" means for
 * a person, and that the role filter reads CURRENT roles only.
 */
function PeopleList() {
  const navigate = useNavigate();
  const { scope, setScope, canToggle } = usePortfolioScope();
  const list = useQuery(
    trpc.people.list.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load the people list' }, staleTime: 10_000 },
    ),
  );
  const people = list.data ?? NO_PEOPLE;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SubjectFilter>('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sort, setSort] = useState<SortState<ColumnId>>(DEFAULT_SORT);
  const today = todayIso();
  const catalogue = useMemo(() => personColumns(today), [today]);
  const columns = useColumnState(STORAGE_KEY, catalogue);
  const workspace = useWorkspace({
    route: '/people',
    state: {
      filters: { query, filter, includeArchived, scope },
      columns: columns.state.order,
      sort,
    },
    apply(state) {
      const table = workspaceTable(state, catalogue, DEFAULT_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'all',
        ),
      );
      setIncludeArchived(workspaceFlag(state, 'includeArchived'));
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => (includeArchived ? true : p.status === 'active'))
      .filter((p) => {
        if (filter === 'all') return true;
        // Filter on CURRENT roles only — a former tenant is not a tenant.
        return p.roles.some((r) => isCurrent(r, today) && ROLE_SUBJECT[r.roleType] === filter);
      })
      .filter((p) => {
        if (!q) return true;

        // A ref typed into search resolves directly, however it was typed —
        // "PE000003", "pe3" and "pe-3" all find person 3.
        const asRef = parseRef(q);
        if (asRef) return asRef.entity === 'person' && asRef.sequence === p.refSeq;

        return [
          displayName(p),
          formatRef('person', p.refSeq),
          `${p.firstName} ${p.lastName}`,
          p.email,
          primaryPhone(p),
          ...p.roles.map((r) => r.subjectLabel),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, catalogue));
  }, [people, query, filter, includeArchived, sort, today, catalogue]);

  const total = people.length;
  const summary = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} people`
      : `Showing ${rows.length} of ${total} people`;

  return (
    <ListShell
      title="People"
      subtitle={summary}
      nav={<PeopleNav />}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={catalogue}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/people/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Add person
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search name, ref (PE000003), email, phone or property"
      searchLabel="Search people"
      filters={
        <FilterChips label="Filter by role" value={filter} options={FILTERS} onChange={setFilter} />
      }
      trailing={
        <ToolbarToggle checked={includeArchived} onChange={setIncludeArchived}>
          Include archived
        </ToolbarToggle>
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `No one matches “${query}”` : 'No people yet'}
          action={
            <Link to="/people/new">
              <Button size="sm">Add person</Button>
            </Link>
          }
        >
          {query
            ? 'Try a different name, ref, email, phone number or property address.'
            : 'People are the base record. Tenants, owners and contractor contacts are all roles attached to a person.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(person) => person.id}
        onOpen={(person) =>
          void navigate({
            to: '/people/$ref',
            params: { ref: formatRef('person', person.refSeq) },
          })
        }
        caption={`People, one row per person. ${summary}.`}
        minWidth="52rem"
      />
    </ListShell>
  );
}
